import { useEffect, useRef, useState } from "react";

/**
 * One burst of confetti over the whole window, in the brand colours.
 *
 * Two cannons fire from the bottom corners towards the middle, the pieces
 * tumble down and fade, and the canvas is gone after a few seconds — it is a
 * moment, not a decoration, so nothing keeps running once the last piece has
 * fallen. It sits above everything and takes no clicks, so the screen under it
 * stays usable while it plays.
 *
 * Nothing is drawn under `prefers-reduced-motion: reduce`: a screen full of
 * spinning shapes is exactly what that setting asks us not to do, and the
 * celebration still reads from the words around it.
 *
 * Drawn on a canvas rather than as DOM nodes, so a burst of a few hundred
 * pieces costs one layer and no layout.
 */

/** The logo's stripes (`global.css`), with their values in case a token is missing. */
const COLORS = [
  ["--orange", "#FFA415"],
  ["--pink", "#F13FAA"],
  ["--magenta", "#C345E9"],
  ["--purple", "#9076F7"],
  ["--blue", "#57BCFF"],
] as const;

const PIECES_PER_CANNON = 90;
const DURATION_MS = 4000;
const GRAVITY = 0.35;
const DRAG = 0.985;

type Piece = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  width: number;
  height: number;
  angle: number;
  spin: number;
  /** Phase of the flip around the piece's own axis, which is what makes it flutter. */
  tilt: number;
  tiltSpeed: number;
  color: string;
};

const brandColors = () => {
  const style = getComputedStyle(document.documentElement);
  return COLORS.map(
    ([token, fallback]) => style.getPropertyValue(token).trim() || fallback,
  );
};

const fire = (width: number, height: number, colors: string[]): Piece[] => {
  const pieces: Piece[] = [];
  // Scale the launch to the window, so the burst reaches the middle of a
  // phone and of a wide screen alike.
  const power = Math.sqrt(height) * 1.15;
  for (const side of [-1, 1]) {
    const originX = side < 0 ? 0 : width;
    for (let i = 0; i < PIECES_PER_CANNON; i++) {
      // Up and inwards: 55°–80° from the horizontal, towards the centre.
      const angle = ((55 + Math.random() * 25) * Math.PI) / 180;
      const speed = power * (0.55 + Math.random() * 0.6);
      pieces.push({
        x: originX,
        y: height,
        vx: -side * Math.cos(angle) * speed,
        vy: -Math.sin(angle) * speed,
        width: 6 + Math.random() * 6,
        height: 8 + Math.random() * 8,
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.3,
        tilt: Math.random() * Math.PI * 2,
        tiltSpeed: 0.08 + Math.random() * 0.12,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }
  }
  return pieces;
};

export function Confetti() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [playing, setPlaying] = useState(
    () => !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!playing || !canvas || !context) return;

    const ratio = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;
    canvas.width = width * ratio;
    canvas.height = height * ratio;
    context.scale(ratio, ratio);

    const pieces = fire(width, height, brandColors());
    const start = performance.now();
    let frame = 0;

    const draw = (now: number) => {
      const elapsed = now - start;
      // Everything fades over the last third, so the burst ends softly
      // rather than with pieces vanishing mid-air.
      const opacity = Math.min(1, (DURATION_MS - elapsed) / (DURATION_MS / 3));
      context.clearRect(0, 0, width, height);
      if (opacity <= 0) {
        setPlaying(false);
        return;
      }
      context.globalAlpha = opacity;
      for (const piece of pieces) {
        piece.vx *= DRAG;
        piece.vy = piece.vy * DRAG + GRAVITY;
        piece.x += piece.vx;
        piece.y += piece.vy;
        piece.angle += piece.spin;
        piece.tilt += piece.tiltSpeed;
        if (piece.y > height + 20) continue;

        context.save();
        context.translate(piece.x, piece.y);
        context.rotate(piece.angle);
        context.scale(1, Math.cos(piece.tilt));
        context.fillStyle = piece.color;
        context.fillRect(
          -piece.width / 2,
          -piece.height / 2,
          piece.width,
          piece.height,
        );
        context.restore();
      }
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  if (!playing) return null;
  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none fixed inset-0 z-60 h-full w-full"
    />
  );
}
